// UseOfFundsDonut.jsx
import { useEffect, useRef, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import gsap from "gsap";

const RAW_DATA = [
  { name: "Production",               value: 40, amount: 12_000_000 },
  { name: "Above-the-Line",           value: 25, amount: 7_500_000 },
  { name: "Post-Production",          value: 15, amount: 4_500_000 },
  { name: "Marketing & Distribution", value: 12, amount: 3_600_000 },
  { name: "Contingency & Insurance",  value: 8,  amount: 2_400_000 },
];

const COLORS = ["#cf2b2b", "#4b34e1", "#ff6a00", "#23c025", "#888888"];

// r=86 → outer stroke edge (86 + strokeWidth/2=4) = 90 = Pie innerRadius ✓
const DIAL_R   = 175;  // stroke center = pie outerRadius ✓
const DIAL_ARC = +(2 * Math.PI * DIAL_R * (140 / 360)).toFixed(2); // ≈ 824
const CX = 180, CY = 180;  // center of 360×360 SVG
const HUB_R = 40;

function TaxDial({ targetProgress }) {
  const [progress, setProgress] = useState(0);
  const animRef = useRef(null);

  useEffect(() => {
    if (targetProgress === 0) return;
    let start = null;
    const duration = 1000;
    const ease = t => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    const step = ts => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      setProgress(ease(p));
      if (p < 1) animRef.current = requestAnimationFrame(step);
    };
    animRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animRef.current);
  }, [targetProgress]);

  const offset   = DIAL_ARC * (1 - progress);
  const angleDeg = 97 + 270 * progress;
  const rad      = (angleDeg - 90) * (Math.PI / 180);
  // needle tip stops just short of the arc so it doesn't bleed over the stroke
  const nx = CX + (DIAL_R - 14) * Math.cos(rad);
  const ny = CY + (DIAL_R - 14) * Math.sin(rad);

  return (
    <svg width="360" height="360" viewBox="0 0 360 360">
      {/* Track */}
      <circle cx={CX} cy={CY} r={DIAL_R}
        fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="8" strokeLinecap="round"
        strokeDasharray={DIAL_ARC} strokeDashoffset="0"
        transform={`rotate(135 ${CX} ${CY})`} />
      {/* Progress arc */}
      <circle cx={CX} cy={CY} r={DIAL_R}
        fill="none" stroke="#1D9E75" strokeWidth="8" strokeLinecap="round"
        strokeDasharray={DIAL_ARC} strokeDashoffset={offset}
        transform={`rotate(135 ${CX} ${CY})`} />
      {/* Needle — renders BEFORE hub so hub paints over its inner half */}
      <line x1={CX} y1={CY} x2={nx} y2={ny}
        stroke="#1D9E75" strokeWidth="4.5" strokeLinecap="round" />
      {/* Hub — paints ON TOP of needle base, keeping text clear */}
      <circle cx={CX} cy={CY} r={HUB_R}
        fill="rgba(20,19,19,0.92)" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" />
      <text x={CX} y={CY - 5} textAnchor="middle"
        fill="rgba(255,255,255,0.5)" fontSize="9" letterSpacing="1.5" fontFamily="sans-serif">
        TAX CREDIT
      </text>
      <text x={CX} y={CY + 20} textAnchor="middle"
        fill="#1D9E75" fontSize="26" fontWeight="700" fontFamily="sans-serif">
        {(30 * progress).toFixed(0)}%
      </text>
    </svg>
  );
}

export default function UseOfFundsDonut() {
  const containerRef = useRef();
  const animatedRef  = useRef(RAW_DATA.map(d => ({ ...d, value: 0 })));
  const [animatedData,  setAnimatedData]  = useState(RAW_DATA.map(d => ({ ...d, value: 0 })));
  const [activeIndex,   setActiveIndex]   = useState(null);
  const [dialTriggered, setDialTriggered] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      setDialTriggered(true);
      const tl = gsap.timeline();
      RAW_DATA.forEach((slice, i) => {
        tl.to(animatedRef.current[i], {
          value: slice.value, duration: 1, ease: "power2.out",
          onUpdate: () => setAnimatedData(animatedRef.current.map(d => ({ ...d })))
        }, 0);
      });
      observer.disconnect();
    }, { threshold: 0.1 });
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const active = activeIndex !== null ? RAW_DATA[activeIndex] : null;

  return (
    <div style={{ width: "100%", display: "flex" }}>

      {/* Chart */}
      <div ref={containerRef}
        style={{ flex: 1, position: "relative", minWidth: 420,
          filter: "drop-shadow(0 0 5px rgba(192,192,192,0.55))", }}>
        <ResponsiveContainer width="100%" height={425}>
          <PieChart>
            <Pie
              data={animatedData}
              dataKey="value"
              nameKey="name"
              innerRadius={90}    // hole diameter = 180px = TaxDial SVG size ✓
              outerRadius={175}
              paddingAngle={3}
              stroke="rgba(210,210,220,0.6)"
              strokeWidth={1.5}
              onMouseEnter={(_, index) => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(null)}
            >
              {animatedData.map((entry, index) => (
                <Cell key={index} fill={COLORS[index]}
                  opacity={activeIndex === null || activeIndex === index ? 1 : 0.35} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        {/* Center overlay — TaxDial by default, slice info on hover */}
        <div style={{
          position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          pointerEvents: "none", zIndex: 2,
        }}>
          {active ? (
            <div style={{ color: "white", textAlign: "center", width: 140 }}>
              <div style={{ fontSize: "13px", opacity: 0.6, marginBottom: 3, lineHeight: 1.2 }}>
                {active.name}
              </div>
              <div style={{ fontSize: "30px", fontWeight: "bold", lineHeight: 1 }}>
                {active.value}%
              </div>
              <div style={{ fontSize: "14px", opacity: 0.75, marginTop: 4 }}>
                ${(active.amount / 1_000_000).toFixed(1)}M
              </div>
            </div>
          ) : (
            <TaxDial targetProgress={dialTriggered ? 1 : 0} />
          )}
        </div>
      </div>

   <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: "1rem", minHeight: 0, }}>
      <h10 style={{ color: 'white', textAlign: 'center', marginBottom: '0rem' }}>
        Texas Moving Image Industry Incentives
      </h10>
     <ul style={{ color: "white", margin: 0, paddingLeft: '1.2rem', lineHeight: '1.8', textAlign: 'left' }}>
  <li>Base Grant- 25% <strong>$7.5M</strong></li>
  <li>Rural filming grant- 2.5% <strong>$750K</strong></li>
  <li>Post-production Bonus- 1% <strong>$300K</strong></li>
  <li>Total Incentive received <strong>$8.55M</strong></li>
</ul>
      </div>

  

    </div>
  );
}